import { defineStore, acceptHMRUpdate } from 'pinia'
import { useTasksStore } from './tasks'
import { usePlanner } from './planner'
import { useImperialUnitsStore } from './imperialUnits'
import { DEFAULT_TRACK_WIDTH } from './drawing'

export const useWarningPlanner = defineStore('warningPlanner', {
    state: () => ({
        warning: null,
        fieldConfig: [],
        selectedTask: null,

    }),
    actions: {
        getTaskWidth(taskType) {
            if (taskType === 'nexco') {
                return 15.5 // NEXCO working width with safety margin
            }
            
            // take the width from the task type (e.g. planting_14m)
            const widthPart = taskType.split('_')[1]
            if (!widthPart) return null
            
            // take only the number from the width part (e.g. 14m -> 14)
            const width = parseInt(widthPart.replace('m', ''))
            return isNaN(width) ? null : width
        },
        getAvailableDistance(headlandIndex) {
            const ts = useTasksStore()
            if (!this.fieldConfig || headlandIndex < 0 || headlandIndex >= this.fieldConfig.length) {
                return 0
            }

            const trackWidth = ts.field.track_width || DEFAULT_TRACK_WIDTH
            let distance = 0
            // sum the distances of the headlands from outer border to the selected headland
            for (let i = 0; i <= headlandIndex; i++) {
                if (this.fieldConfig[i] !== undefined) {
                    if (i === 0) {
                        // First headland has special distances to outer border: 0.5 -> ~4m, 1 -> ~7.5m
                        distance += this.fieldConfig[i] === 0.5 ? trackWidth * 0.25 + 0.4 : trackWidth * 0.5 + 0.4
                    } else {
                        // Other headlands: distance between them is config[i] * trackWidth
                        distance += this.fieldConfig[i] * trackWidth
                    }
                }
            }
            return distance
        },
        findRecommendedHeadland(halfWidth) {
            if (!this.fieldConfig) return null
            
            // find the first headland where the implement fits, starting from the outer border
            for (let i = 0; i < this.fieldConfig.length; i++) {
                const availableDistance = this.getAvailableDistance(i)
                if (halfWidth < availableDistance) {
                    return i + 1 // index starts from 0
                }
            }
            return null
        },
        createWarning({ taskWidth, halfWidth, availableDistance, remainingDistance, selectedHeadland, recommendedHeadland }) {
            const imp = useImperialUnitsStore()
            
            return {
                messageKey: 'Warning: The {taskWidth} implement ({halfWidth} on each side) exceeds or equals the available distance of {availableDistance} for headland {headland}.',
                messageParams: {
                    taskWidth: imp.formatDistanceMetersFeet(taskWidth),
                    halfWidth: imp.formatDistanceMetersFeet(halfWidth),
                    availableDistance: imp.formatDistanceMetersFeet(availableDistance),
                    headland: selectedHeadland
                },
                riskKey: 'Risk of exiting field boundary',
                level: 'High',
                taskWidth,
                halfWidth,
                availableDistance,
                remainingDistance,
                selectedHeadland,
                recommendedHeadland: recommendedHeadland || null
            }
        },
        createSuccess({ taskWidth, halfWidth, availableDistance, remainingDistance, selectedHeadland }) {
            const imp = useImperialUnitsStore()

            return {
                messageKey: 'The {taskWidth} implement ({halfWidth} on each side) fits within the available distance of {availableDistance} for headland {headland}.',
                messageParams: {
                    taskWidth: imp.formatDistanceMetersFeet(taskWidth),
                    halfWidth: imp.formatDistanceMetersFeet(halfWidth),
                    availableDistance: imp.formatDistanceMetersFeet(availableDistance),
                    headland: selectedHeadland
                },
                riskKey: 'Safe to use this headland',
                level: 'Safe',
                taskWidth,
                halfWidth,
                availableDistance,
                remainingDistance,
                selectedHeadland,
                recommendedHeadland: null
            }
        },
        createCritical({ taskWidth, halfWidth, availableDistance, remainingDistance, selectedHeadland, recommendedHeadland }) {
            const imp = useImperialUnitsStore()
            
            return {
                messageKey: 'Info: The {taskWidth} implement ({halfWidth} on each side) significantly exceeds the available distance of {availableDistance} for headland {headland}. The implement will extend beyond the field boundary by {remainingDistance}.',
                messageParams: {
                    taskWidth: imp.formatDistanceMetersFeet(taskWidth),
                    halfWidth: imp.formatDistanceMetersFeet(halfWidth),
                    availableDistance: imp.formatDistanceMetersFeet(availableDistance),
                    headland: selectedHeadland,
                    remainingDistance: imp.formatDistanceMetersFeet(Math.abs(remainingDistance))
                },
                riskKey: 'Critical risk of exiting field boundary',
                level: 'Critical',
                taskWidth,
                halfWidth,
                availableDistance,
                remainingDistance,
                selectedHeadland,
                recommendedHeadland: recommendedHeadland || null
            }
        },
        createAutonomousWarning({ taskWidth, halfWidth, availableDistance, remainingDistance, selectedHeadland, recommendedHeadland }) {
            const imp = useImperialUnitsStore()
            return {
                messageKey: 'Warning: Autonomous route is enabled, i.e. the implement needs {halfWidth} to either side. The available distance for headland {headland} is {availableDistance}. The remaining distance is {remainingDistance}.',
                messageParams: {
                    taskWidth: imp.formatDistanceMetersFeet(taskWidth),
                    halfWidth: imp.formatDistanceMetersFeet(halfWidth + 7.0), // here it should be a fixed 7m
                    availableDistance: imp.formatDistanceMetersFeet(availableDistance),
                    headland: selectedHeadland,
                    remainingDistance: imp.formatDistanceMetersFeet(remainingDistance)
                },
                riskKey: 'Critical risk: No autonomous route possible.',
                level: 'Critical',
                taskWidth,
                halfWidth,
                availableDistance,
                remainingDistance,
                selectedHeadland,
                recommendedHeadland: recommendedHeadland || null
            }
        },
        checkWarnings() {
            const ts = useTasksStore()
            const ps = usePlanner()
        
            this.fieldConfig = ts.field.config
            this.selectedTask = ps.routeData.task_type
            
            // Reset warning
            this.warning = null
            
            // Skip if no task selected or no headland target
            if (!this.selectedTask || !ps.headlandTarget) {
                return
            }
            
            // Get task width
            const taskWidth = this.getTaskWidth(this.selectedTask)
            if (!taskWidth) {
                return
            }
            
            // Get selected headland index (0-based)
            const selectedHeadlandIndex = ps.headlandTarget - 1
            
            // Get available distance for selected headland
            const availableDistance = this.getAvailableDistance(selectedHeadlandIndex)
            if (availableDistance === 0) {
                return
            }
            
            // Calculate half width and remaining distance
            const halfWidth = taskWidth / 2
            const autonomousRoute = ps.makeAutonomousRoute
            const remainingDistance = availableDistance - halfWidth - (autonomousRoute ? 7.0 : 0)
            
            // Find recommended headland
            const distanceToHeadland = autonomousRoute ? halfWidth + 7.0 : halfWidth
            const recommendedHeadland = this.findRecommendedHeadland(distanceToHeadland)
            // Check if there's a risk
            if (remainingDistance < 0 && autonomousRoute) {
                this.warning = this.createAutonomousWarning({
                    taskWidth,
                    halfWidth,
                    availableDistance,
                    remainingDistance,
                    selectedHeadland: ps.headlandTarget,
                    recommendedHeadland
                })
            } else if (halfWidth >= availableDistance) {
                // Check if remaining distance is negative (critical)
                if (remainingDistance < 0) {
                    // Create and set critical warning
                    this.warning = this.createCritical({
                        taskWidth,
                        halfWidth,
                        availableDistance,
                        remainingDistance,
                        selectedHeadland: ps.headlandTarget,
                        recommendedHeadland
                    })
                } else {
                    // Create and set warning
                    this.warning = this.createWarning({
                        taskWidth,
                        halfWidth,
                        availableDistance,
                        remainingDistance,
                        selectedHeadland: ps.headlandTarget,
                        recommendedHeadland
                    })
                }
            } else {
                // Create and set success
                this.warning = this.createSuccess({
                    taskWidth, 
                    halfWidth, 
                    availableDistance, 
                    remainingDistance, 
                    selectedHeadland: ps.headlandTarget
                })
            }
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useWarningPlanner, import.meta.hot))
}
